/**
 * CellRenderer Component
 *
 * Dispatcher component that renders the appropriate cell type based on column type.
 * Acts as a facade for all cell components to simplify usage in board views.
 */

import type { CellRendererProps } from './types';
import { StatusCell } from './StatusCell';
import { PriorityCell } from './PriorityCell';
import { PeopleCell } from './PeopleCell';
import { DateCell } from './DateCell';
import { TimelineCell } from './TimelineCell';
import { NumberCell } from './NumberCell';
import { TextCell } from './TextCell';
import { LinkCell } from './LinkCell';
import { CheckboxCell } from './CheckboxCell';
import { DropdownCell } from './DropdownCell';
import { FilesCell } from './FilesCell';
import { TagsCell } from './TagsCell';
import { FormulaCell } from './FormulaCell';

export function CellRenderer({
  columnType,
  value,
  settings,
  onChange,
  readonly = false,
  availablePeople,
  files,
  tagSuggestions,
  onFileUpload,
  onFileRemove,
}: CellRendererProps) {
  switch (columnType) {
    case 'status':
      return (
        <StatusCell
          value={value as { labelId: string; label: string; color: string } | null}
          onChange={onChange as ((value: { labelId: string; label: string; color: string } | null) => void) | undefined}
          labels={settings?.labels}
          readonly={readonly}
        />
      );

    case 'priority':
      return (
        <PriorityCell
          value={value as { priority: 'critical' | 'high' | 'medium' | 'low' | 'none' } | null}
          onChange={onChange as ((value: { priority: 'critical' | 'high' | 'medium' | 'low' | 'none' } | null) => void) | undefined}
          readonly={readonly}
        />
      );

    case 'people':
      return (
        <PeopleCell
          value={value as { personIds: string[] } | null}
          onChange={onChange as ((value: { personIds: string[] } | null) => void) | undefined}
          availablePeople={availablePeople}
          multiple={settings?.multiple ?? true}
          readonly={readonly}
        />
      );

    case 'date':
      return (
        <DateCell
          value={value as { date: string; time?: string } | null}
          onChange={onChange as ((value: { date: string; time?: string } | null) => void) | undefined}
          includeTime={settings?.includeTime}
          dateFormat={settings?.dateFormat}
          readonly={readonly}
        />
      );

    case 'timeline':
      return (
        <TimelineCell
          value={value as { start: string; end: string } | null}
          onChange={onChange as ((value: { start: string; end: string } | null) => void) | undefined}
          dateFormat={settings?.dateFormat}
          readonly={readonly}
        />
      );

    case 'numbers':
      return (
        <NumberCell
          value={value as { value: number; unit?: string } | null}
          onChange={onChange as ((value: { value: number; unit?: string } | null) => void) | undefined}
          unit={settings?.unit}
          decimals={settings?.decimals}
          format={settings?.format}
          readonly={readonly}
        />
      );

    case 'text':
      return (
        <TextCell
          value={value as { text: string } | null}
          onChange={onChange as ((value: { text: string } | null) => void) | undefined}
          readonly={readonly}
        />
      );

    case 'link':
      return (
        <LinkCell
          value={value as { url: string; text?: string } | null}
          onChange={onChange as ((value: { url: string; text?: string } | null) => void) | undefined}
          readonly={readonly}
        />
      );

    case 'checkbox':
      return (
        <CheckboxCell
          value={value as { checked: boolean } | null}
          onChange={onChange as ((value: { checked: boolean } | null) => void) | undefined}
          readonly={readonly}
        />
      );

    case 'dropdown':
      return (
        <DropdownCell
          value={value as { selectedIds: string[] } | null}
          onChange={onChange as ((value: { selectedIds: string[] } | null) => void) | undefined}
          options={settings?.options}
          multiple={settings?.multiple ?? false}
          readonly={readonly}
        />
      );

    case 'files':
      return (
        <FilesCell
          value={value as { fileIds: string[] } | null}
          onChange={onChange as ((value: { fileIds: string[] } | null) => void) | undefined}
          files={files}
          onUpload={onFileUpload}
          onRemove={onFileRemove}
          readonly={readonly}
        />
      );

    case 'tags':
      return (
        <TagsCell
          value={value as { tags: string[] } | null}
          onChange={onChange as ((value: { tags: string[] } | null) => void) | undefined}
          suggestions={tagSuggestions}
          readonly={readonly}
        />
      );

    case 'formula':
      return (
        <FormulaCell
          value={value as { formula: string; result: string | number | boolean | null; error?: string } | null}
          outputType={settings?.outputType}
        />
      );

    default:
      // Unknown column type
      return (
        <div className="w-full h-full flex items-center">
          <span className="text-gray-400 text-sm">-</span>
        </div>
      );
  }
}
